import { useEffect } from 'react';
import { Link, Navigate, Route, Routes, useLocation, useParams } from 'react-router';
import { hasAuthParams, useAuth } from 'react-oidc-context';
import { Alert, Loading } from './components/ui';
import { Layout } from './components/Layout';
import { AppFooter } from './components/AppFooter';
import { Logo } from './components/Logo';
import { signinState } from './config';
import { StructureLabels, useI18n } from './i18n';
import { AboutPage } from './pages/AboutPage';
import { AutomationMapPage } from './pages/AutomationMapPage';
import { SalesDocPage, SalesToolsPage } from './pages/SalesToolsPage';
import { ComparatorPage } from './pages/ComparatorPage';
import { CallGuidePage } from './pages/CallGuidePage';
import { DemoScriptPage } from './pages/DemoScriptPage';
import { ApprovalPage, ApprovalsPage } from './pages/ApprovalPage';
import { BankPage } from './pages/finances/BankPage';
import { BudgetPage } from './pages/finances/BudgetPage';
import { FinancesPage } from './pages/finances/FinancesPage';
import { JournalPage } from './pages/finances/JournalPage';
import { MeetingPage } from './pages/governance/MeetingPage';
import { MeetingsPage } from './pages/governance/MeetingsPage';
import { AssetsPage } from './pages/maintenance/AssetsPage';
import { CalendarPage } from './pages/maintenance/CalendarPage';
import { LogbookPage } from './pages/maintenance/LogbookPage';
import { VendorsPage } from './pages/maintenance/VendorsPage';
import { WorkOrderPage } from './pages/maintenance/WorkOrderPage';
import { WorkOrdersPage } from './pages/maintenance/WorkOrdersPage';
import { InsurancePage } from './pages/InsurancePage';
import { MemberPage } from './pages/MemberPage';
import { PartiesPage } from './pages/PartiesPage';
import { HelpPage } from './pages/HelpPage';
import { ConsolePage } from './pages/ConsolePage';
import { ManualPage } from './pages/ManualPage';
import { AutomationPage } from './pages/AutomationPage';
import { OnboardingPage } from './pages/OnboardingPage';
import { RegistersPage } from './pages/governance/RegistersPage';
import { WrittenPage } from './pages/governance/WrittenPage';
import { ProfilePage } from './pages/ProfilePage';
import { ReservationsPage } from './pages/ReservationsPage';
import { QuotePartsPage } from './pages/QuotePartsPage';
import { RequestDetailPage } from './pages/RequestDetailPage';
import { RequestsPage } from './pages/RequestsPage';
import { DashboardPage } from './pages/DashboardPage';
import { DocumentsPage } from './pages/DocumentsPage';
import { SettingsPage } from './pages/SettingsPage';
import { UnitPage } from './pages/UnitPage';
import { UnitsPage } from './pages/UnitsPage';
import { VendorPage } from './pages/VendorPage';
import { SyndicateProvider, useMe } from './session';

export function App() {
  // Contractors open a work order's secure link without signing in.
  if (useLocation().pathname.startsWith('/v/')) {
    return (
      <Routes>
        <Route path="/v/:token" element={<VendorPage />} />
      </Routes>
    );
  }
  return <SignedInApp />;
}

function SignedInApp() {
  const auth = useAuth();
  const { t, locale } = useI18n();

  // Every page requires a session: send unauthenticated visitors straight to Keycloak.
  useEffect(() => {
    if (!auth.isLoading && !auth.isAuthenticated && !auth.error && !hasAuthParams() && !auth.activeNavigator) {
      void auth.signinRedirect({ ui_locales: locale, state: signinState() });
    }
  }, [auth, locale]);

  if (auth.error) {
    return (
      <div className="centered">
        <Logo height={40} />
        <Alert>{t.signInError}</Alert>
        <button type="button" className="button" onClick={() => void auth.signinRedirect({ ui_locales: locale, state: signinState() })}>
          {t.retry}
        </button>
      </div>
    );
  }
  if (!auth.isAuthenticated) {
    return (
      <div className="centered">
        <Logo height={40} />
        <Loading label={t.signingIn} />
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/about/automatisations" element={<AutomationMapPage />} />
      <Route path="/manual" element={<ManualPage />} />
      <Route path="/ventes" element={<SalesToolsPage />} />
      <Route path="/ventes/comparateur" element={<ComparatorPage />} />
      <Route path="/ventes/guide" element={<CallGuidePage />} />
      <Route path="/ventes/demo" element={<DemoScriptPage />} />
      <Route path="/ventes/doc/:name" element={<SalesDocPage />} />
      <Route path="/s/:slug" element={<SyndicateShell />}>
        <Route index element={<DashboardPage />} />
        <Route path="approvals" element={<ApprovalsPage />} />
        <Route path="approvals/:approvalId" element={<ApprovalPage />} />
        <Route path="requests" element={<RequestsPage />} />
        <Route path="requests/:requestId" element={<RequestDetailPage />} />
        <Route path="documents" element={<DocumentsPage />} />
        <Route path="units" element={<UnitsPage />} />
        <Route path="units/:unitId" element={<UnitPage />} />
        <Route path="maintenance" element={<WorkOrdersPage />} />
        <Route path="maintenance/work-orders/:workOrderId" element={<WorkOrderPage />} />
        <Route path="maintenance/calendar" element={<CalendarPage />} />
        <Route path="maintenance/logbook" element={<LogbookPage />} />
        <Route path="maintenance/assets" element={<AssetsPage />} />
        <Route path="maintenance/vendors" element={<VendorsPage />} />
        <Route path="governance" element={<MeetingsPage />} />
        <Route path="governance/written" element={<WrittenPage />} />
        <Route path="governance/registers" element={<RegistersPage />} />
        <Route path="governance/:meetingId" element={<MeetingPage />} />
        <Route path="reservations" element={<ReservationsPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="parties" element={<PartiesPage />} />
        <Route path="parties/:partyId" element={<MemberPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="help" element={<HelpPage />} />
        <Route path="automation" element={<AutomationPage />} />
        <Route path="onboarding" element={<OnboardingPage />} />
        <Route path="insurance" element={<InsurancePage />} />
        <Route path="quote-parts" element={<QuotePartsPage />} />
        <Route path="finances" element={<FinancesPage />} />
        <Route path="finances/budget" element={<BudgetPage />} />
        <Route path="finances/bank" element={<BankPage />} />
        <Route path="finances/journal" element={<JournalPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/** After login: open the only syndicate directly, or let the user choose. */
function Home() {
  const { t, locale, setLocale } = useI18n();
  const me = useMe();
  if (me.isPending) return <Loading label={t.loading} />;
  if (me.isError) return <Alert>{t.common.error}</Alert>;

  // Syndiqia staff land on their console (demos, clients, support accesses, team): docs/engineering/roles.md.
  if (me.data.staff.length > 0) return <ConsolePage me={me.data} />;
  const memberships = me.data.memberships;
  if (memberships.length === 0)
    return (
      <div className="centered">
        <Alert tone="info">{t.noSyndicate}</Alert>
        <AppFooter />
      </div>
    );
  if (memberships.length === 1) return <Navigate to={`/s/${memberships[0]!.slug}`} replace />;
  return (
    <div className="chooser">
      <header className="chooser-header">
        <Logo height={44} />
        <button type="button" className="link-button" onClick={() => setLocale(locale === 'fr' ? 'en' : 'fr')}>
          {t.language}
        </button>
      </header>
      <h1>{t.chooseSyndicate}</h1>
      <p className="muted">{t.chooseIntro}</p>
      <ul className="chooser-grid">
        {memberships.map((m) => (
          <li key={m.tenantId}>
            <Link to={`/s/${m.slug}`} className={`chooser-card chooser-${m.legalStructure}`}>
              <span className="chooser-icon" aria-hidden="true">
                {m.legalStructure === 'divided_coownership' ? <BuildingIcon /> : <HousesIcon />}
              </span>
              <span className="chooser-kind">{t.chooseKinds[m.legalStructure]}</span>
              <strong className="chooser-name">{m.name}</strong>
              <span className="muted small">
                {t.roleLabel(m.roles.length)}
                {m.roles.map((r) => t.roles[r]).join(', ')}
              </span>
              <span className="chooser-open">{t.chooseOpen} →</span>
            </Link>
          </li>
        ))}
      </ul>
      <AppFooter />
    </div>
  );
}

/** A condo building (syndicate) and a row of houses (association), drawn in the brand's teal. */
function BuildingIcon() {
  return (
    <svg viewBox="0 0 48 48" width="40" height="40" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinejoin="round">
      <rect x="10" y="6" width="28" height="36" rx="2" />
      <path d="M16 13h4M28 13h4M16 20h4M28 20h4M16 27h4M28 27h4M21 42v-8h6v8" strokeLinecap="round" />
    </svg>
  );
}
function HousesIcon() {
  return (
    <svg viewBox="0 0 48 48" width="40" height="40" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinejoin="round">
      <path d="M4 24 14 15l10 9v16H4z" />
      <path d="M24 24l10-9 10 9v16H24" />
      <path d="M11 40v-7h6v7M31 40v-7h6v7" strokeLinecap="round" />
    </svg>
  );
}

function SyndicateShell() {
  const { t } = useI18n();
  const { slug } = useParams();
  const me = useMe();
  if (me.isPending) return <Loading label={t.loading} />;
  if (me.isError) return <Alert>{t.common.error}</Alert>;

  // Also accept the technical id, so links from before readable addresses keep working.
  const membership = me.data.memberships.find((m) => m.slug === slug || m.tenantId === slug);
  if (!membership) return <Navigate to="/" replace />;
  if (slug !== membership.slug) return <Navigate to={`/s/${membership.slug}`} replace />;
  return (
    <SyndicateProvider membership={membership}>
      <StructureLabels structure={membership.legalStructure}>
        <Layout />
      </StructureLabels>
    </SyndicateProvider>
  );
}
